import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder, FormGroup } from '@angular/forms';
import { WithdrawalAdminService } from '../../_core/_services/withdrawal-admin.service';
import { NotificationService } from '../../_core/_services/notification.service';
import {
  WithdrawalAdminDetailDto,
  CompleteWithdrawalRequestDto,
  FailWithdrawalRequestDto
} from '../../_core/_model/withdrawal';
import { prettifyFieldKey } from '../../_core/_helpers/withdrawal-field.helper';

@Component({
  selector: 'app-withdrawal-detail',
  standalone: false,
  templateUrl: './withdrawal-detail.html',
  styleUrl: './withdrawal-detail.css'
})
export class WithdrawalDetail implements OnInit {
  withdrawalId = '';
  loading = false;
  actionLoading = false;

  item: WithdrawalAdminDetailDto | null = null;

  showCompleteModal = false;
  showFailModal = false;
  actionForm!: FormGroup;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private fb: FormBuilder,
    private withdrawalAdminService: WithdrawalAdminService,
    private notifyService: NotificationService
  ) {}

  ngOnInit(): void {
    this.withdrawalId = this.route.snapshot.paramMap.get('id') || '';
    this.buildForm();
    this.loadDetail();
  }

  buildForm(): void {
    this.actionForm = this.fb.group({
      gatewayReference: [''],
      providerResponseJson: [''],
      failureReason: ['']
    });
  }

  loadDetail(): void {
    if (!this.withdrawalId) {
      this.notifyService.showError('Withdrawal id is missing', 'Error');
      return;
    }

    this.loading = true;

    this.withdrawalAdminService.getWithdrawalRequestById(this.withdrawalId).subscribe({
      next: (res) => {
        this.item = res.data;
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.Error?.Details || err?.error?.message || 'Failed to load withdrawal detail',
          'Error'
        );
        this.item = null;
      },
      complete: () => {
        this.loading = false;
      }
    });
  }

  openCompleteModal(): void {
    if (!this.item) return;

    this.actionForm.reset({
      gatewayReference: this.item.gatewayReference || '',
      providerResponseJson: '',
      failureReason: ''
    });

    this.showCompleteModal = true;
  }

  openFailModal(): void {
    if (!this.item) return;

    this.actionForm.reset({
      gatewayReference: '',
      providerResponseJson: '',
      failureReason: this.item.failureReason || ''
    });

    this.showFailModal = true;
  }

  closeModals(): void {
    this.showCompleteModal = false;
    this.showFailModal = false;
  }

  completeRequest(): void {
    if (!this.item) return;

    this.actionLoading = true;

    const payload: CompleteWithdrawalRequestDto = {
      gatewayReference: this.toNullableString(this.actionForm.value.gatewayReference),
      providerResponseJson: this.toNullableString(this.actionForm.value.providerResponseJson)
    };

    this.withdrawalAdminService.completeWithdrawalRequest(this.item.withdrawalId, payload).subscribe({
      next: () => {
        this.notifyService.showSuccess('Withdrawal marked as completed', 'Success');
        this.closeModals();
        this.loadDetail();
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.Error?.Details || err?.error?.message || 'Failed to complete withdrawal',
          'Error'
        );
      },
      complete: () => {
        this.actionLoading = false;
      }
    });
  }

  failRequest(): void {
    if (!this.item) return;

    this.actionLoading = true;

    const payload: FailWithdrawalRequestDto = {
      failureReason: this.toNullableString(this.actionForm.value.failureReason)
    };

    this.withdrawalAdminService.failWithdrawalRequest(this.item.withdrawalId, payload).subscribe({
      next: () => {
        this.notifyService.showSuccess('Withdrawal marked as failed', 'Success');
        this.closeModals();
        this.loadDetail();
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.Error?.Details || err?.error?.message || 'Failed to fail withdrawal',
          'Error'
        );
      },
      complete: () => {
        this.actionLoading = false;
      }
    });
  }

  retryRequest(): void {
    if (!this.item) return;

    this.actionLoading = true;

    this.withdrawalAdminService.retryWithdrawalRequest(this.item.withdrawalId).subscribe({
      next: () => {
        this.notifyService.showSuccess('Withdrawal retry triggered', 'Success');
        this.loadDetail();
      },
      error: (err) => {
        this.notifyService.showError(
          err?.error?.Error?.Details || err?.error?.message || 'Failed to retry withdrawal',
          'Error'
        );
      },
      complete: () => {
        this.actionLoading = false;
      }
    });
  }

  canComplete(): boolean {
    const status = (this.item?.status || '').toLowerCase();
    return status === 'pending' || status === 'processing';
  }

  canFail(): boolean {
    const status = (this.item?.status || '').toLowerCase();
    return status === 'pending' || status === 'processing';
  }

  canRetry(): boolean {
    return (this.item?.status || '').toLowerCase() === 'failed';
  }

  badgeClass(status: string): string {
    switch ((status || '').toLowerCase()) {
      case 'completed':
      case 'confirmed':
        return 'bg-success-subtle text-success';
      case 'failed':
      case 'cancelled':
        return 'bg-danger-subtle text-danger';
      case 'processing':
      case 'pending':
        return 'bg-warning-subtle text-warning';
      default:
        return 'bg-secondary-subtle text-secondary';
    }
  }

  destinationEntries(): { key: string; value: any }[] {
    if (!this.item?.destinationDetailsJson) return [];

    try {
      const parsed = JSON.parse(this.item.destinationDetailsJson);
      return Object.entries(parsed).map(([key, value]) => ({ key, value }));
    } catch {
      return [];
    }
  }

  pretty(key: string): string {
    return prettifyFieldKey(key);
  }

  back(): void {
    this.router.navigate(['/admin/withdrawals/requests']);
  }

  private toNullableString(value: any): string | null {
    const v = String(value ?? '').trim();
    return v ? v : null;
  }
}